import React from 'react';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import LabelIcon from '@mui/icons-material/LabelOutlined';

const SemanticTableCard = ({
  record,
  database,
  onView,
  onEdit,
  onDelete,
  isDeleted
}) => {
  const tableLabel = record?.display_name || record?.table_name || record?.table_id || 'Sin nombre';
  const statusLabel = isDeleted
    ? 'Eliminada'
    : record?.soft_delete?.deleted
      ? 'Eliminada'
      : record?.is_active === false
        ? 'Inactiva'
        : 'Activa';
  const statusColor = statusLabel === 'Activa' ? 'var(--wb-success-strong)' : statusLabel === 'Inactiva' ? 'var(--wb-warning)' : 'var(--wb-danger)';
  const columnsCount = Array.isArray(record?.columns) ? record.columns.length : 0;
  const tags = Array.isArray(record?.tags) ? record.tags : [];
  const deletedAt = record?.soft_delete?.deleted_at;

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'var(--wb-bg-card)',
        borderRadius: 2,
        p: 2,
        mb: 2,
        border: '1px solid var(--wb-overlay-10)'
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid
          item
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              bgcolor: 'var(--wb-x-bg-6366f126, rgba(99, 102, 241, 0.15))',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <TableChartIcon sx={{ color: 'var(--wb-x-text-818cf8, #818cf8)' }} />
          </Box>
        </Grid>
        <Grid item xs>
          <Stack spacing={0.75}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" color="var(--wb-text-strong)" sx={{ fontWeight: 600 }}>
                {tableLabel}
              </Typography>
              <Chip
                label={statusLabel}
                size="small"
                sx={{
                  backgroundColor: 'var(--wb-overlay-08)',
                  color: statusColor,
                  borderRadius: 1
                }}
              />
              {record?.version && (
                <Chip
                  label={`Versión ${record.version}`}
                  size="small"
                  sx={{ backgroundColor: 'var(--wb-x-bg-94a3b82e, rgba(148, 163, 184, 0.18))', color: 'var(--wb-text-label)' }}
                />
              )}
            </Box>
            <Typography variant="body2" color="var(--wb-text-soft)">
              {record?.description || 'Sin descripción registrada.'}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              alignItems="center"
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <StorageIcon sx={{ color: 'var(--wb-muted)', fontSize: 18 }} />
                <Typography variant="body2" color="var(--wb-text-soft)">
                  {database?.name || database?.display_name || database?.semantic_database_id || 'Base semántica desconocida'}
                </Typography>
              </Stack>
              <Typography variant="body2" color="var(--wb-text-soft)">
                {`${record?.database || 'db'} · ${record?.schema || 'schema'}`}
              </Typography>
              <Typography variant="body2" color="var(--wb-text-soft)">
                {columnsCount === 1 ? '1 columna' : `${columnsCount} columnas`}
              </Typography>
            </Stack>
            {tags.length > 0 && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    icon={<LabelIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      backgroundColor: 'var(--wb-x-bg-3b82f626, rgba(59, 130, 246, 0.15))',
                      color: 'var(--wb-x-text-bfdbfe, #bfdbfe)',
                      '& .MuiChip-icon': { color: 'var(--wb-blue-light)' }
                    }}
                  />
                ))}
              </Stack>
            )}
            {isDeleted && deletedAt && (
              <Typography variant="caption" color="var(--wb-x-text-f87171d9, rgba(248, 113, 113, 0.85))">
                Eliminada el {new Date(deletedAt).toLocaleString()}
              </Typography>
            )}
          </Stack>
        </Grid>
        <Grid item>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Ver detalles">
              <IconButton onClick={() => onView?.(record)} sx={{ color: 'var(--wb-text-strong)' }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar" disableHoverListener={isDeleted}>
              <span>
                <IconButton
                  onClick={() => onEdit?.(record)}
                  sx={{ color: 'var(--wb-text-strong)' }}
                  disabled={isDeleted}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Eliminar" disableHoverListener={isDeleted}>
              <span>
                <IconButton
                  onClick={() => onDelete?.(record)}
                  sx={{ color: isDeleted ? 'var(--wb-text-ghost)' : 'var(--wb-danger)' }}
                  disabled={isDeleted}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SemanticTableCard;
